import { useState } from "react";
import { useUser, useClerk } from "@clerk/clerk-react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPortal } from "../lib/api";
import { usePlan } from "../App";
import ConfirmModal from "../components/ConfirmModal";

export default function Settings() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const plan = usePlan();

  const portalMut = useMutation({
    mutationFn: createPortal,
    onSuccess: ({ url }) => { window.location.href = url; },
  });

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "40px 24px" }}>
        <Link to="/" style={backLink}>← Back</Link>
        <h1 style={{ margin: "0 0 32px", fontSize: 26, fontWeight: 700, color: "#EFF2F5" }}>Settings</h1>

        <Section title="Account">
          <Row label="Email">{user?.emailAddresses[0]?.emailAddress}</Row>
          <Row label="Plan">
            <span style={{ fontWeight: 600, color: plan === "pro" ? "#39D98A" : "#EFF2F5" }}>
              {plan.charAt(0).toUpperCase() + plan.slice(1)}
            </span>
            {plan !== "pro" && (
              <Link to="/upgrade" style={{ marginLeft: 12, fontSize: 13, color: "#FF2566", textDecoration: "none" }}>Upgrade →</Link>
            )}
          </Row>
        </Section>

        {plan === "pro" && (
          <Section title="Billing">
            <p style={{ margin: "0 0 14px", fontSize: 14, color: "#98A1AB" }}>
              Manage your subscription, download invoices, or cancel via the Stripe customer portal.
            </p>
            <button
              onClick={() => portalMut.mutate()}
              disabled={portalMut.isPending}
              className="btn-run"
              style={{ padding: "10px 20px", fontSize: 14 }}
            >
              {portalMut.isPending ? "Opening portal…" : "Manage Billing"}
            </button>
            {portalMut.error && (
              <p style={{ color: "#FF5C5C", fontSize: 13, marginTop: 8 }}>Failed to open billing portal.</p>
            )}
          </Section>
        )}

        <Section title="Account Actions">
          <button onClick={() => setConfirmSignOut(true)} className="btn-run" style={{ padding: "10px 20px", fontSize: 14 }}>
            Sign out
          </button>
        </Section>
      </div>

      {confirmSignOut && (
        <ConfirmModal
          title="Sign out?"
          message="You'll need to sign back in to access your run history and monitors."
          confirmLabel="Sign out"
          destructive
          onConfirm={() => signOut({ redirectUrl: "/" })}
          onClose={() => setConfirmSignOut(false)}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="glass" style={{ padding: "20px 24px", marginBottom: 20 }}>
      <h2 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</h2>
      {children}
    </div>
  );
}

const backLink: React.CSSProperties = { display: "inline-block", marginBottom: 20, fontSize: 13, color: "#98A1AB", textDecoration: "none" };

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
      <span style={{ fontSize: 14, color: "#98A1AB" }}>{label}</span>
      <span style={{ fontSize: 14, color: "#EFF2F5" }}>{children}</span>
    </div>
  );
}
